import React, { useState, useCallback } from "react";
import { Container, Title, Text, TextInput, LoadingOverlay, Alert, Button, Group, Space, Badge, HoverCard } from '@mantine/core';
import { Calendar } from '@mantine/dates';
import { IconDownload } from "@tabler/icons";
import { useQuery } from "@tanstack/react-query";
import { CSVLink } from "react-csv";
import * as App from "./../model/App";
import { PatentDocketTable, PatentDocketTableColumns, TableDaysRemainingCell } from "./../components";

export default function PatentDocketScreen() {
    const { isLoading, isError, data } = useQuery(["patents"], async () => { 
        const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
        return App.Firebase.Read.getPatents(userID); 
    });
    const [searchText, setSearchText] = useState<string>("");
    const [calendar1Value, setCalendar1Value] = useState<Date>(new Date());
    const [calendar2Value, setCalendar2Value] = useState<Date>(App.Util.DateUtils.addMonthsToDate(new Date(), 1));

    // Convert data to a CSV-displayable format...
    const getCsvData = useCallback(() => {
        if (!data) { return []; }

        // Get an array of { deadline, patent } elements...
        const patents = Object.keys(data).map(key => data[key]);
        let deadlines = patents.map((patent: App.Data.App.IPatent) => {
            if (!patent.deadlines) { return []; }
            return patent.deadlines
                .filter(d => !d.isComplete)
                .map((deadline: App.Data.App.IDeadline) => {
                    return { deadline: deadline, data: patent };
                 });

        }).flat();

        // Sort and filter...
        deadlines = deadlines
            .sort((a, b) => {
                if (!a.deadline.dueDate) { return 1; }
                if (!b.deadline.dueDate) { return -1; }
                return a.deadline.dueDate - b.deadline.dueDate;
            })

            // Filter based on search text...
            .filter((deadline) => {
                if (searchText.length === 0) { return true; }
                if (deadline.deadline.title.toLowerCase().includes(searchText.toLowerCase())) { return true; }
                if (deadline.data.title.toLowerCase().includes(searchText.toLowerCase())) { return true; }
                if (App.Data.App.PatentUtils.searchMatchesPatent(searchText, deadline.data)) { return true; }
                return false;
            });

            return App.Data.App.DeadlineUtils.toCsvFormat(deadlines);
    }, [data, searchText]);


    // Build data for the table...
    const docketData = React.useMemo((): PatentDocketTableColumns[] => {
        if (!data) { return []; }

        // Get an array of { deadline, patent } elements...
        const patents = Object.keys(data).map(key => data[key]);
        let deadlines = patents.map((patent: App.Data.App.IPatent) => {
            if (!patent.deadlines) { return []; }
            return patent.deadlines
                .filter(d => !d.isComplete)
                .map((deadline: App.Data.App.IDeadline) => {
                    return { deadline: deadline, data: patent };
                 });

        }).flat();

        // Sort and filter...
        deadlines = deadlines
            .sort((a, b) => {
                if (!a.deadline.dueDate) { return 1; }
                if (!b.deadline.dueDate) { return -1; }
                return a.deadline.dueDate - b.deadline.dueDate;
            })

            // Filter based on search text...
            .filter((deadline) => {
                if (searchText.length === 0) { return true; }
                if (deadline.deadline.title.toLowerCase().includes(searchText.toLowerCase())) { return true; }
                if (deadline.data.title.toLowerCase().includes(searchText.toLowerCase())) { return true; }
                if (App.Data.App.PatentUtils.searchMatchesPatent(searchText, deadline.data)) { return true; }
                return false;
            });

        return deadlines.map(deadlineWithPatent => {
            const numDaysRemaining = (deadlineWithPatent.deadline.dueDate)
                ? App.Util.DateUtils.daysUntil(new Date(deadlineWithPatent.deadline.dueDate))
                : 0;

            return {
                tmTitle: deadlineWithPatent.data.title,
                grantingNumber: deadlineWithPatent.data.grantingNumber,
                applicationNumber: deadlineWithPatent.data.applicationNumber,
                action: deadlineWithPatent.deadline.title,
                description: deadlineWithPatent.deadline.description,
                daysRemaining: <TableDaysRemainingCell daysRemaining={numDaysRemaining} daysRemainingOverrideMessage="" />,
                dueDate: deadlineWithPatent.deadline.dueDate ? App.Data.App.DateUtils.formatDate(new Date(deadlineWithPatent.deadline.dueDate)) : "Unknown"
            };
        });
    }, [data, searchText]);

    // If calendar 1 changes, set calendar 2 to one month later...
    const onCalendar1Change = useCallback((value: Date) => {
        setCalendar1Value(value);
        setCalendar2Value(App.Util.DateUtils.addMonthsToDate(value, 1));
    }, []);

    // If calendar 2 changes, set calendar 1 to one month earlier...
    const onCalendar2Change = useCallback((value: Date) => {
        setCalendar2Value(value);
        setCalendar1Value(App.Util.DateUtils.addMonthsToDate(value, -1));
    }, []);

    return (
        <Container ml={0} mr={30} style={{ maxWidth: "100%" }}>
            <Group position="apart">
                <Title>Patent Deadlines</Title>
            </Group>
            
            <LoadingOverlay visible={isLoading} />
            {isError && <Alert color="red" title="Error">Error loading data</Alert>}
            <Space h="md" />

            {/* Calendars */}
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>

            {/* Calendar 1 */}
            <Calendar 
                month={calendar1Value}
                onMonthChange={onCalendar1Change}
                onChange={onCalendar1Change}
                weekendDays={[]}
                renderDay={(date) => {
                    const day = date.getDate();

                    // If today's date, show a red badge...
                    const isToday = App.Data.App.DateUtils.isToday(date);
                    const color = isToday ? "red" : "blue";

                    // Check if there is a deadline on this date...
                    const deadlines = docketData.filter(d => d.dueDate === App.Data.App.DateUtils.formatDate(date));
                    return (deadlines.length > 0) 
                        ? <PatentCalendarHoverCard day={day} deadlines={deadlines} color={color} />
                        : (isToday) ? <Badge color="red">{day}</Badge> : <Text>{day}</Text>;
                  }}
            />

            {/* Calendar 2 */}
            <Calendar 
                month={calendar2Value}
                onMonthChange={onCalendar2Change}
                onChange={onCalendar2Change}
                weekendDays={[]}
                renderDay={(date) => {
                    const day = date.getDate();

                    // If today's date, show a red badge...
                    const isToday = App.Data.App.DateUtils.isToday(date);
                    const color = isToday ? "red" : "blue";

                    // Check if there is a deadline on this date...
                    const deadlines = docketData.filter(d => d.dueDate === App.Data.App.DateUtils.formatDate(date));
                    return (deadlines.length > 0) 
                        ? <PatentCalendarHoverCard day={day} deadlines={deadlines} color={color} />
                        : (isToday) ? <Badge color="red">{day}</Badge> : <Text>{day}</Text>;
                  }}
            />
            </div>
            <Space h="md" />

            <Space h="md" />
            {data && Object.keys(data).length === 0 && <Text>No deadlines found</Text>}
            {data && Object.keys(data).length > 0 && <>
                <Group position="right">
                    <CSVLink data={getCsvData()} filename="aestrada_patent_docket">
                        <Button leftIcon={<IconDownload size={14} />} variant="subtle">Download csv</Button>
                    </CSVLink>
                    <TextInput placeholder="Search" value={searchText} onChange={(event) => { setSearchText(event.target.value); }}  />
                </Group>
                <PatentDocketTable data={docketData} />
                </>
            }
        </Container>
    );
}

interface IPatentCalendarHoverCardProps {
    day: number;
    color: "blue" | "red";
    deadlines: PatentDocketTableColumns[];
}

function PatentCalendarHoverCard(props: IPatentCalendarHoverCardProps) {
    const { day, color, deadlines } = props;

    return (
        <HoverCard width={280} shadow="md">
            <HoverCard.Target>
                <Badge color={color}>{day}</Badge>
            </HoverCard.Target>
            <HoverCard.Dropdown>
            {deadlines.map((deadline) => {
                return (
                    <Text size="sm">
                        {deadline.applicationNumber}; {deadline.grantingNumber}; {deadline.tmTitle}
                    </Text>
                );
            })}
            </HoverCard.Dropdown>
        </HoverCard>
    );
}